// Library document page: header with title, family, version and status chips and actions (requirements, PDF, download, what changed, and Quality for Operator and Admin) and the overview, contents (with the clause count for Operator and Admin), figures, tables, standards and thresholds tabs kept in the URL.
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { BadgeCheckIcon, Columns2Icon, FileTextIcon, HistoryIcon, LibraryIcon } from "lucide-react";
import type { ReactNode } from "react";

import type { DocumentOut, DocumentStats } from "@/api/__generated__";
import {
  catalogGetDocumentOptions,
  catalogGetDocumentStatsOptions,
  catalogListFamiliesOptions,
  clausesGetClauseOptions,
} from "@/api/__generated__/@tanstack/react-query.gen";
import { regionParam, useRegion } from "@/app/region";
import { useCrumbOverride } from "@/app/shell/Breadcrumbs";
import { useInternalDetails } from "@/app/useInternalDetails";
import { ContextChip, ContextChips } from "@/components/common/ContextChips";
import { DownloadButton } from "@/components/common/DownloadButton";
import { PageHeader } from "@/components/common/PageHeader";
import { ProblemState } from "@/components/common/ProblemState";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ContentsTab } from "@/features/library/ContentsTab";
import { DocumentOverviewTab } from "@/features/library/DocumentOverviewTab";
import { DocumentStatusBadge } from "@/features/library/DocumentStatusBadge";
import { FiguresTab } from "@/features/library/FiguresTab";
import { StandardsTab } from "@/features/library/StandardsTab";
import { TablesTab } from "@/features/library/TablesTab";
import { ThresholdsTab } from "@/features/library/ThresholdsTab";
import { formatCompact } from "@/lib/format";

export const DOCUMENT_TABS = [
  "overview",
  "contents",
  "figures",
  "tables",
  "standards",
  "thresholds",
] as const;
export type DocumentTab = (typeof DOCUMENT_TABS)[number];

export interface DocumentSearch {
  tab: DocumentTab;
  clause?: string | undefined;
  preview: boolean;
}

interface DocumentPageProps {
  docUid: string;
  search: DocumentSearch;
  onSearchChange: (search: Partial<DocumentSearch>, options?: { replace?: boolean }) => void;
}

const TAB_LABELS: Record<DocumentTab, string> = {
  overview: "Overview",
  contents: "Contents",
  figures: "Figures",
  tables: "Tables",
  standards: "Standards",
  thresholds: "Thresholds",
};

function tabCount(tab: DocumentTab, stats: DocumentStats | undefined, internal: boolean): number | null {
  if (stats === undefined) {
    return null;
  }
  switch (tab) {
    case "contents":
      return internal ? stats.counts.clauses : null;
    case "figures":
      return stats.counts.figures;
    case "tables":
      return stats.counts.tables;
    case "standards":
      return stats.counts.standards;
    case "thresholds":
      return stats.counts.thresholds;
    default:
      return null;
  }
}

function HeaderSkeleton() {
  return (
    <div aria-hidden className="flex flex-col gap-2.5">
      <Skeleton className="h-3 w-20" />
      <Skeleton className="h-8 w-96 max-w-full" />
      <div className="flex gap-1.5">
        {[0, 1, 2, 3, 4].map((index) => (
          <Skeleton key={index} className="h-[26px] w-28 rounded-pill" />
        ))}
      </div>
    </div>
  );
}

function DocumentChips({ document }: { document: DocumentOut }) {
  return (
    <ContextChips className="mt-3">
      <ContextChip icon={LibraryIcon}>{document.family_title ?? document.family_id}</ContextChip>
      <ContextChip value={<span className="font-mono">{document.version_label}</span>}>Version</ContextChip>
      <DocumentStatusBadge status={document.status} className="h-[26px]" />
    </ContextChips>
  );
}

function DocumentActions({ document, region }: { document: DocumentOut; region: string }) {
  const internal = useInternalDetails();
  return (
    <>
      <Button asChild>
        <Link to="/$region/workspace" params={{ region }} search={{ doc: document.doc_uid }}>
          <Columns2Icon aria-hidden />
          Open requirements
        </Link>
      </Button>
      <Button asChild variant="outline">
        <Link to="/$region/library/$docUid/pdf" params={{ region, docUid: document.doc_uid }}>
          <FileTextIcon aria-hidden />
          Original PDF
        </Link>
      </Button>
      <DownloadButton
        variant="outline"
        path={`/api/v1/documents/${encodeURIComponent(document.doc_uid)}/pdf`}
        fallbackName={document.file_name ?? `${document.family_id}_${document.version_label}.pdf`}
        label="Download PDF"
      />
      <Button asChild variant="outline">
        <Link to="/$region/versions" params={{ region }} search={{ family: document.family_id }}>
          <HistoryIcon aria-hidden />
          What changed
        </Link>
      </Button>
      {internal ? (
        <Button asChild variant="outline">
          <Link to="/$region/quality" params={{ region }} search={{ doc: document.doc_uid }}>
            <BadgeCheckIcon aria-hidden />
            Quality
          </Link>
        </Button>
      ) : null}
    </>
  );
}

function TabLabel({ label, count }: { label: string; count: number | null }): ReactNode {
  return (
    <>
      {label}
      {count === null ? null : (
        <span className="rounded-pill bg-muted px-1.5 text-label/[18px] font-semibold text-ink-2 tabular-nums">
          {formatCompact(count)}
        </span>
      )}
    </>
  );
}

export function DocumentPage({ docUid, search, onSearchChange }: DocumentPageProps) {
  const { jurisdiction } = useRegion();
  const region = regionParam(jurisdiction);
  const internal = useInternalDetails();
  const document = useQuery(catalogGetDocumentOptions({ path: { doc_uid: docUid } }));
  const stats = useQuery(catalogGetDocumentStatsOptions({ path: { doc_uid: docUid } }));
  const families = useQuery(
    catalogListFamiliesOptions({ query: { jurisdiction: jurisdiction.jurisdiction_id } }),
  );
  const clauseUid = search.clause ?? null;
  const clause = useQuery({
    ...clausesGetClauseOptions({ path: { clause_uid: clauseUid ?? "" } }),
    enabled: search.tab === "contents" && clauseUid !== null,
  });
  const family = families.data?.find((item) => item.family_id === document.data?.family_id);
  const publisher = family?.publisher_name ?? document.data?.issued_by ?? null;
  const crumbs = document.data
    ? [
        "Library",
        document.data.title,
        ...(search.tab === "contents" && clause.data ? [clause.data.clause.number] : []),
      ]
    : null;
  useCrumbOverride(crumbs);

  if (document.isError) {
    return (
      <div className="mx-auto max-w-[1440px] px-4 pt-6 pb-16 md:px-7">
        <Card>
          <ProblemState
            problem={document.error}
            retry={() => {
              void document.refetch();
            }}
          />
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[1600px] animate-fade-in px-4 pt-6 pb-10 md:px-7">
      {document.isPending ? (
        <HeaderSkeleton />
      ) : (
        <>
          <PageHeader
            eyebrow={
              <Link to="/$region/library" params={{ region }} className="hover:underline">
                Library
              </Link>
            }
            title={document.data.title}
            actions={<DocumentActions document={document.data} region={region} />}
          />
          <DocumentChips document={document.data} />
        </>
      )}
      <Tabs
        className="mt-5 gap-3.5"
        value={search.tab}
        onValueChange={(value) => {
          onSearchChange({ tab: value as DocumentTab });
        }}
      >
        <TabsList variant="underline" aria-label="Document views" className="w-full border-b">
          {DOCUMENT_TABS.map((tab) => (
            <TabsTrigger key={tab} value={tab}>
              <TabLabel label={TAB_LABELS[tab]} count={tabCount(tab, stats.data, internal)} />
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="overview">
          <DocumentOverviewTab
            docUid={docUid}
            region={region}
            document={document.data}
            publisher={publisher}
            stats={stats.data}
            isPending={stats.isPending}
            error={stats.error}
            onRetry={() => {
              void stats.refetch();
            }}
          />
        </TabsContent>
        <TabsContent value="contents">
          <ContentsTab
            docUid={docUid}
            region={region}
            clauseUid={clauseUid}
            preview={search.preview}
            onSelect={(uid) => {
              onSearchChange({ clause: uid });
            }}
            onPreviewChange={(preview) => {
              onSearchChange({ preview }, { replace: true });
            }}
          />
        </TabsContent>
        <TabsContent value="figures">
          <FiguresTab docUid={docUid} region={region} />
        </TabsContent>
        <TabsContent value="tables">
          <TablesTab docUid={docUid} />
        </TabsContent>
        <TabsContent value="standards">
          <StandardsTab docUid={docUid} />
        </TabsContent>
        <TabsContent value="thresholds">
          <ThresholdsTab docUid={docUid} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
